import { INDENT_EM_PER_LEVEL } from '@root/helpers/editor';
import { Extension } from '@tiptap/core';
import type { SingleCommands } from '@tiptap/react';
import { getNodeAttributes } from '@tiptap/react';

export interface IndentOptions {
  types: string[];
  defaultLevel: number;
}

export const Indent = Extension.create<IndentOptions>({
  name: 'indent',
  priority: 97,

  addOptions() {
    return {
      types: [
        'table', // to deal with Tab shortcut as well
        'paragraph',
        'li',
      ],
      defaultLevel: 0,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          level: {
            default: this.options.defaultLevel,
            parseHTML: (element: any) => {
              const level = element.getAttribute('level');
              if (level === undefined || level === null) {
                return this.options.defaultLevel;
              }
              return parseInt(level, 10);
            },
            renderHTML: (attributes: any) => {
              if (attributes.level === this.options.defaultLevel || !attributes.indent) {
                return {};
              }

              // hanging pulls the first line back `hanging` levels, Word-style; clamped to
              // `level` so the first line cannot cross the container's left edge
              const hanging = Math.min(Number(attributes.hanging) || 0, attributes.level);

              return {
                style:
                  `padding-left: ${attributes.level * INDENT_EM_PER_LEVEL}em` +
                  (hanging > 0 ? `; text-indent: -${hanging * INDENT_EM_PER_LEVEL}em` : ''),
                level: attributes.level,
                indent: attributes.indent,
                ...(hanging > 0 ? { hanging } : {}),
              };
            },
          },
          indent: {
            default: false,
            parseHTML(element: any) {
              const indent = element.getAttribute('indent');
              return indent;
            },
            renderHTML(_attributes: any) {
              return {};
            },
          },
          hanging: {
            default: false,
            parseHTML(element: any) {
              const value = element.getAttribute('hanging');
              if (!value) {
                return false;
              }
              return parseInt(value, 10) || false;
            },
            renderHTML(_attributes: any) {
              return {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indentLeftOrGoToPreviousCell:
        () =>
        ({ commands }: { commands: SingleCommands }) =>
          commands.goToPreviousCell() || commands.indentLeft(),
      indentRightOrGoToNextCell:
        () =>
        ({ commands }: { commands: SingleCommands }) =>
          commands.goToNextCell() || commands.indentRight(),
      indentRight:
        () =>
        ({ commands }: { commands: SingleCommands }) => {
          const isListItemActive = this.editor.isActive('listItem');
          if (isListItemActive) {
            return commands.indentOrderedListRight();
          }
          return this.options.types.every((type: string) => {
            if (type === 'table' || type === 'li') {
              return true;
            }
            const attrs = getNodeAttributes(this.editor.state, type);
            const level = attrs.level ?? 0;
            return commands.updateAttributes(type, { level: level + 1, indent: true });
          });
        },
      indentLeft:
        () =>
        ({ commands }: { commands: SingleCommands }) => {
          const isListItemActive = this.editor.isActive('listItem');
          if (isListItemActive) {
            return commands.indentOrderedListLeft();
          }
          return this.options.types.every((type: string) => {
            if (type === 'table' || type === 'li') {
              return true;
            }
            const attrs = getNodeAttributes(this.editor.state, type);
            const level = attrs.level ?? 0;
            const finalLevel = level <= 0 ? 0 : level - 1;
            const hanging = Math.min(Number(attrs.hanging) || 0, finalLevel) || false;
            return commands.updateAttributes(type, { level: finalLevel, indent: true, hanging });
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        this.editor.commands.indentRightOrGoToNextCell();
        return this.editor.commands.focus();
      },
      'Shift-Tab': () => {
        this.editor.commands.indentLeftOrGoToPreviousCell();
        return this.editor.commands.focus();
      },
    };
  },
});
